import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import FeatureHeader from './FeatureHeader';
import Tile from './Tile';
import { SLOW, useReveal } from './useReveal';
import './features.css';
import './studentView.css';

/* "What students see." (teacher landing, after F4 and before the FAQ;
   text on the LEFT, since F4's is on the right). Closes the page's story
   from the STUDENT's side: F3 found AL, SN and DP stuck on factoring
   (they wrote x = -2 and -3 for x^2 - 5x + 6 = 0), F4 assigned them
   factoring practice due Friday; here Sam N. (SN) works that set, gets the
   question right this time, and moves up from Level 2 to Level 3
   (SAMPLE DATA CANON, DECISIONS.md).

   A simplified replica of the REAL student screens (no device frame):
   - the homework player (src/StudentDashboardV3/HomeworkPlayerPage.jsx):
     the set's title and due line, the "N of M" progress bar, the question
     (QuestionCard), A-D choices (a pick turns blue; after Check the right
     answer turns green), and the coach dock under it: "Pick the best
     answer, then check it." + Check, then "Nice work!" (praiseFor) + Next.
     No Buffy: the homework player passes showAiTutor={false}, and the AI
     tutor is a Premium add-on for self-serve students only (premiumGate).
   - the Answer Explanation (QuestionCard's feedback block), set beside the
     question (the page's rule: the example left, the explanation right).
   - the student's level in the domain: "Level N" and the 7 level boxes of
     the subject page (StudentSubjectPage: done blue, current light blue,
     locked gray), with the product's "Level up!" line.
   Scene choices: the correct pick shows a "Correct" word, not the
   product's check glyph (the page has no icons); the page's ink pill
   stands in for the product's blue button (as F2 does).

   The loop (~14 s at the 1.35x pace) is a pure function of one clock `t`
   (frameAt), like F3 and F4: pausing (off screen) stops the clock. ?settled=1 and reduced motion draw the final frame.

   Layout follows the tile's own width (as F2): 680px+ the student's screen
   left and a column right with the explanation above the level card (both
   slots always laid out, so nothing moves when one fills in); narrower,
   one slot under the screen that shows the level card, hands over to the
   explanation after Check, then takes the level card back for the level
   up. The ?settled=1 phone frame stacks both (the tile grows; it is
   static). */

const MINUS = '−';
const NBSP = String.fromCharCode(160);
// the product's default homework length (CustomHomework: 5 questions)
const TOTAL = 5;
const DONE_BEFORE = 2;
const CHOICES = [
  { key: 'A', value: `x = ${MINUS}2 and ${MINUS}3` },
  { key: 'B', value: 'x = 2 and 3', correct: true },
  { key: 'C', value: 'x = 1 and 6' },
  { key: 'D', value: `x = ${MINUS}1 and 6` },
];
const MAX_LEVEL = 7;
const LEVEL_FROM = 2;
const SPLIT_AT = 680; // tile width at which the screen and the column sit side by side

// base ms; the clock runs SLOW (1.35x) slower than these numbers
const T_PICK = 1000;
const T_PRESS = 2000; // Check presses
const T_CHECK = 2350; // the verdict
const T_EXPLAIN = 3000;
const T_BACK = 5800; // narrow layout: the slot hands back to the level card
const T_LEVEL = 6500;
const T_NEXT = 9300; // Next presses
const T_OUT = 9700; // the stateful parts fade, then everything resets
const LOOP = 10400;

function frameAt(t) {
  return {
    picked: t >= T_PICK,
    press: t >= T_PRESS && t < T_CHECK,
    checked: t >= T_CHECK,
    explained: t >= T_EXPLAIN,
    back: t >= T_BACK,
    leveled: t >= T_LEVEL,
    next: t >= T_NEXT,
    out: t >= T_OUT,
  };
}
const STILL = { ...frameAt(T_NEXT - 1) };
const keyOf = (f) => Object.keys(f).map((k) => (f[k] ? 1 : 0)).join('');

function useLoopClock(playing, still, paused) {
  const [frame, setFrame] = useState(() => (still ? STILL : frameAt(0)));
  const clock = useRef(0);
  useEffect(() => {
    if (still) {
      setFrame(STILL);
      return undefined;
    }
    if (!playing) return undefined;
    let raf = 0;
    let last = performance.now();
    let key = '';
    const tick = (now) => {
      clock.current += (now - last) / SLOW;
      last = now;
      if (clock.current >= LOOP) clock.current = 0;
      const f = frameAt(clock.current);
      const k = keyOf(f);
      if (k !== key) {
        key = k;
        setFrame(f);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, still]);
  // a pause inside the closing fade holds the last full frame instead of a
  // half-faded one; play resumes from there
  useEffect(() => {
    if (!paused || still || clock.current < T_OUT) return;
    clock.current = T_OUT - 1;
    setFrame(frameAt(T_OUT - 1));
  }, [paused, still]);
  return frame;
}

function Choice({ c, f }) {
  const picked = c.correct && f.picked;
  const ok = c.correct && f.checked;
  return (
    <li className={`cxf-q-choice cxv-choice${picked ? ' is-picked cxv-press' : ''}`}>
      {picked ? <span className="cxv-ring is-sel cxv-a-fade" /> : null}
      {ok ? <span className="cxv-ring is-ok cxv-a-fade" /> : null}
      <span className="cxf-q-key">
        {c.key}
        {picked ? <span className="cxf-q-keyfill cxv-keyfill cxv-a-fade">{c.key}</span> : null}
      </span>
      <span className="cxf-q-val">{c.value}</span>
      {ok ? <span className="cxv-ok cxv-a-pop">Correct</span> : null}
    </li>
  );
}

function Screen({ f }) {
  const answered = DONE_BEFORE + (f.checked ? 1 : 0);
  const pressing = f.press || f.next;
  return (
    <div className="cx-surface cxv-win cxs-in-panel">
      <div className="cxv-head">
        <div className="cxv-title">Factoring practice</div>
        <div className="cxv-due">Due Friday, 11:59 PM</div>
        <div className="cxv-prog cxv-fade">
          <span className="cxv-track">
            <span className="cxv-fill" style={{ transform: `scaleX(${answered / TOTAL})` }} />
          </span>
          <span className="cxv-count">
            <span key={answered} className={f.checked ? 'cxv-a-fade' : undefined}>{`${answered} of ${TOTAL}`}</span>
          </span>
        </div>
      </div>
      <div className="cxv-body">
        <div className="cxf-q-stem">
          x<sup>2</sup> {MINUS} 5x + 6 = 0
        </div>
        <div className="cxf-q-ask">What are the solutions to the given equation?</div>
        <ul className="cxf-q-choices cxv-choices cxv-fade">
          {CHOICES.map((c) => <Choice key={c.key} c={c} f={f} />)}
        </ul>
      </div>
      <div className="cxv-dock cxv-fade">
        <span className="cxv-dock-ring is-coach" />
        {f.checked ? <span className="cxv-dock-ring is-ok cxv-a-fade" /> : null}
        <span key={f.checked ? 'ok' : 'ask'} className={`cxv-dock-t${f.checked ? ' cxv-a-fade' : ''}`}>
          {f.checked ? 'Nice work!' : 'Pick the best answer, then check it.'}
        </span>
        <span
          key={`${f.checked ? 'next' : 'check'}-${pressing ? 'press' : 'rest'}`}
          className={`cx-action cxv-btn${f.picked ? '' : ' is-off'}${pressing ? ' cxv-press' : ''}`}
        >
          {f.checked ? 'Next' : 'Check'}
        </span>
      </div>
    </div>
  );
}

function Explanation({ shown }) {
  // the wrapper shows / hides (opacity); the card inside plays the entrance
  return (
    <div className={`cxv-expl-wrap${shown ? ' is-shown' : ''}`}>
      <div className="cx-surface cxv-card cxv-expl">
        <div className="cx-window-bar">Answer Explanation</div>
        <div className="cxv-card-body">
          <p className="cxv-expl-t">{`Factor the left side, then set each factor equal to${NBSP}0.`}</p>
          <div className="cxv-work">(x {MINUS} 2)(x {MINUS} 3) = 0</div>
          <div className="cxv-work">x = 2 or x = 3</div>
        </div>
      </div>
    </div>
  );
}

function LevelCard({ leveled, shown }) {
  const level = LEVEL_FROM + (leveled ? 1 : 0);
  // the wrapper shows / hides (the narrow slot); the card plays the reveal
  return (
    <div className={`cxv-lv-wrap${shown ? ' is-shown' : ''}`}>
      <div className="cx-surface cxv-card cxv-level cxs-in-panel" style={{ '--d': '140ms' }}>
        <div className="cx-window-bar">Advanced Math</div>
        <div className="cxv-card-body">
          <div className="cxv-who">
            <span className="cxs-ini" style={{ background: '#FFD6DC' }}>SN</span>
            <span className="cxv-who-name">Sam N.</span>
          </div>
          <div className="cxv-fade">
            <div className="cxv-lv-row">
              <span key={level} className={`cxv-lv-num${leveled ? ' cxv-a-rise' : ''}`}>{`Level ${level}`}</span>
              {leveled ? <span className="cxv-lv-up cxv-a-pop">Level up!</span> : null}
            </div>
            <div className="cxv-boxes">
              {Array.from({ length: MAX_LEVEL }, (_, i) => i + 1).map((n) => (
                <span key={n} className={`cxv-box${n < level ? ' is-done' : ''}${n === level ? ' is-cur' : ''}`}>
                  <i className="cxv-box-cur" />
                  <i className="cxv-box-done" />
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* three short points under the sub (owner 2026-09-30: explain the student
   side a little). Each is true in the product: the Up next list
   (StudentHomePage: "Everything due, in one list ... soonest first"), the
   server-graded Check (HomeworkPlayerPage submitHomeworkAnswer), the
   skill check that unlocks the next level (the band session's last part;
   StudentDomainPage "Unlock Level N"). */
const POINTS = (
  <ul className="cxv-points">
    <li>
      <span className="cxv-points-lead">Your assignments show up.</span>
      {' '}
      In their Up next list, soonest due first.
    </li>
    <li>
      <span className="cxv-points-lead">Feedback right away.</span>
      {' '}
      Each answer is checked the moment they submit it.
    </li>
    <li>
      <span className="cxv-points-lead">Levels that move up.</span>
      {' '}
      Passing a skill check unlocks the next level.
    </li>
  </ul>
);

/* The scene alone (schools page 2026-10-01: its feature tabs show it
   without this section's heading). */
export function StudentViewScene({ wall = 'violet' }) {
  const { ref, reveal, live, near, still } = useReveal({ amount: 0.35 });
  const playing = reveal === 'play' && live;
  const f = useLoopClock(playing, still, false);

  // the tile's own width decides the layout (as F2)
  const tileRef = useRef(null);
  const [split, setSplit] = useState(true);
  const measure = useCallback(() => {
    const tile = tileRef.current;
    if (!tile) return;
    const wide = tile.clientWidth >= SPLIT_AT;
    setSplit((s) => (s === wide ? s : wide));
  }, []);
  useLayoutEffect(() => { measure(); }, [measure]);
  useEffect(() => {
    const tile = tileRef.current;
    if (!tile || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => measure());
    ro.observe(tile);
    return () => ro.disconnect();
  }, [measure]);

  // narrow + looping: one slot (level card, then the explanation, then the
  // level card again for the level up)
  const slot = !split && !still;
  const explShown = f.explained && (!slot || !f.back);
  const levelShown = !slot || !f.explained || f.back;

  return (
    <div ref={ref} className="cxv-stage" data-reveal={reveal} data-live={live ? 'true' : 'false'}>
      <Tile
        ref={tileRef}
        wall={wall}
        near={near}
        pos="100% 0%"
        className={`cxv-tile ${split ? 'is-split' : 'is-stack'}${slot ? ' is-slot' : ''}${still ? ' is-still' : ''}${f.out ? ' is-out' : ''}`}
        aria-hidden="true"
      >
        <span className="cxv-wash" />
        <div className="cxv-grid">
          <Screen f={f} />
          <div className="cxv-side">
            <Explanation shown={explShown} />
            <LevelCard leveled={f.leveled} shown={levelShown} />
          </div>
        </div>
      </Tile>
      <p className="cxs-sr">
        Sample: Sam N., one of the three students, works on the factoring practice due Friday. The question is x squared minus 5x
        plus 6 equals 0: what are the solutions? Sam picks x = 2 and 3 and checks it. It is correct, and the screen says Nice work!
        The answer explanation: factor the left side, (x minus 2)(x minus 3) = 0, so x = 2 or x = 3. Sam&apos;s Advanced Math
        level moves up from Level 2 to Level 3.
      </p>
    </div>
  );
}

/* `header` (schools preview, 2026-09-30): optional FeatureHeader overrides
   (title, sub, side, id, as, labelled, after, className added to ours) so the
   schools page can reuse this scene under its own words. With no props the
   teacher page renders exactly as before. */
export default function StudentViewSection({ header = {}, wall = 'violet' }) {
  return (
    <FeatureHeader
      title={(
        <>
          <span>What students see.</span>
          {' '}
          <span className="cxs-h2-tail">At their own level.</span>
        </>
      )}
      sub="Students join with your class code, then take the assessment that places them on a level in every domain."
      side="left"
      id="cxs-f5"
      after={POINTS}
      {...header}
      className={`cxv-sec${header.className ? ` ${header.className}` : ''}`}
    >
      <StudentViewScene wall={wall} />
    </FeatureHeader>
  );
}
